<template>
    <div>
        <h2>
            Logs
            <small>{{ total }} request{{ total|pluralize() }} found.</small>
        </h2>

        <div class="listing white">
            <form v-if="total > 0 || hasOptions()" @submit.prevent>
                <div class="row">
                    <div class="form-group col-4">
                        <label>Before</label>
                        <input type="text" v-model="options.before" placeholder="YYYY-MM-DD hh:mm" @change="updateRoute(true)" />
                    </div>
                    <div class="form-group col-4">
                        <label>Status</label>
                        <select v-model="options.status" @change="updateRoute(true)">
                            <option value="all">All</option>
                            <option value="error">Failed</option>
                            <option value="success">Successful</option>
                        </select>
                    </div>
                    <div class="form-group col-4">
                        <label>API Key</label>
                        <select v-model="options.key" @change="updateRoute(true)" :disabled="!keys || keys.length === 0">
                            <option value="all">All</option>
                            <option v-for="key in keys" :key="key.token" :value="key.token">{{ key.name || key.token }}</option>
                        </select>
                    </div>
                </div>
            </form>
            <template v-if="total > 0">
                <div class="entries" ref="output">
                    <table>
                        <thead>
                            <tr>
                                <th>&nbsp;</th>
                                <th>Executed</th>
                                <th>Duration</th>
                                <th>Message</th>
                                <th class="grey">Request ID</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="log in logs" :key="log.id" :class="{'failed': !isSuccessful(log)}">
                                <td>
                                    <svg-success v-if="isSuccessful(log)"/>
                                    <svg-error v-else />
                                </td>
                                <td><time :datetime="log.executed|isoformat">{{ log.executed|datetime('MMM DD, HH:mm:ss') }}</time></td>
                                <td>
                                    <span v-if="isSuccessful(log)">{{ (log.duration / 1000)|floating('s') }}</span>
                                    <span v-else>-</span>
                                </td>
                                <td>
                                    <p v-if="isSuccessful(log)">Conversion was successful.</p>
                                    <p v-else>{{ log.error }}</p>
                                </td>
                                <td class="grey">{{ log.request_id }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
            <template v-else>
                <div class="alert alert-success">
                    <template v-if="total > 0 || hasOptions()">Your filters yielded no results.</template>
                    <template v-else>You have no logs at the moment. Run a request toward PDFShift's API and they will appear here.</template>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import SvgSuccess from '@/assets/images/success.svg'
import SvgError from '@/assets/images/error.svg'
import Moment from 'moment'

export default {
    components: { SvgSuccess, SvgError },
    data () {
        return {
            logs: [],
            total: null,
            hasMore: false,
            loading: false,
            keys: [],
            options: {
                before: null,
                cursor: null,
                status: 'all',
                key: 'all'
            }
        }
    },
    created () {
        Object.keys(this.options).forEach(key => {
            if (key in this.$route.query) {
                this.options[key] = this.$route.query[key]
            }
        })

        this.$http.get('account/api/').then(response => {
            this.keys = response.body.keys.data
        })

        this.loadLogs()
    },
    watch: {
        total (to, from) {
            if (to > 0) {
                this.$nextTick(() => {
                    this.$refs.output.addEventListener('scroll', this.onLogScroll)
                })
            } else {
                this.$refs.output.removeEventListener('scroll', this.onLogScroll)
            }
        }
    },
    beforeDestroy () {
        if (this.total > 0) {
            this.$refs.output.removeEventListener('scroll', this.onLogScroll)
        }
    },
    beforeRouteEnter (to, from, next) {
        next(vm => {
            window.scrollTo(0, 0)
        })
    },
    methods: {
        hasOptions () {
            if (this.options.cursor !== null) {
                return true
            }

            if (this.options.before !== null) {
                return true
            }

            if (this.options.status !== 'all') {
                return true
            }

            if (this.options.key !== 'all') {
                return true
            }

            return false
        },
        updateRoute (reset) {
            if (this.$route.fullPath === this.$router.resolve({ query: this.options }).href) {
                return
            }

            const query = this.options
            this.$router.replace({ query }).catch(() => {})
            if (reset) {
                this.logs = []
                this.options.cursor = null
                if (this.$refs.output) {
                    this.$refs.output.scrollTo(0, 0)
                }
            }

            this.loadLogs()
        },
        loadLogs () {
            if (!this.options.cursor) {
                this.logs = []
            }

            this.loading = true

            this.$http.get('logs', { params: { limit: 20, ...this.options } }).then(response => {
                this.logs = this.logs.concat(response.body.logs.data)
                this.total = response.body.logs.total
                this.hasMore = response.body.logs.has_more
                this.loading = false
            })
        },
        bytesConverter (size) {
            if (size < 1000) {
                return `${size}b`
            } else if (size < 1000000) {
                size = (size / 1000).toFixed(2)
                return `${size}kb`
            } else if (size < 1000000000) {
                size = (size / 1000000).toFixed(2)
                return `${size}mb`
            }
        },
        isSuccessful (log) {
            return log.code >= 200 && log.code < 300
        },
        onLogScroll () {
            if (this.loading || !this.hasMore) {
                return
            }
            const elt = this.$refs.output
            if (elt.offsetHeight + elt.scrollTop >= elt.scrollHeight - 100) {
                this.loading = true
                if (this.logs.length) {
                    this.options.cursor = this.logs[this.logs.length - 1].id
                } else {
                    this.options.cursor = null
                }

                this.updateRoute(false)
            }
        },
        setBeforeAt (dt) {
            this.options.before = Moment.utc(dt + 1000).format('YYYY-MM-DD HH:mm:ss')
            this.updateRoute(true)
        }
    }
}
</script>

<style lang="scss" scoped>
    @import '../../public/css/vars';

    .listing form {
        border: none;
        border-radius: 0;
        box-shadow: none;
        padding: 10px;
    }

    .listing .form-group {
        padding: 0 10px;

        &:first-child { padding-left: 0 }
        &:last-child { padding-right: 0 }
    }

    // @see https://dribbble.com/shots/6160594-Line-formats
    .entries {
        max-height: calc(100vh - 350px);
        min-height: 300px;
        overflow-y: scroll;
    }

    .listing table {
        width: 100%;

        thead {
            position: sticky;
            top: 0;
            background-color: #e8ebf0;
            color: #485674;
            z-index: 5000;

            th {
                border-bottom: solid 2px #dde1e8;
                margin-bottom: 5px;
                padding: 10px 10px;
                text-align: left;
                font-weight: 400;
                font-size: 14px;
                line-height: 1.5;
            }
        }
    }

    .listing tbody tr {
        border: solid 1px #dde1e8;
        background-color: fade-out(#dde1e8, 0.8);

        &:nth-child(even) {
            background-color: #e8ebf0;
        }
        &:nth-child(odd) {
            background-color: #f3f5f8;
        }

        td {
            padding: 10px 10px;
            border-top: solid 2px #dde1e8;
            color: #485674;

            svg {
                height: 16px;
                width: 16px;
                stroke: #13aa38;
                flex-shrink: 0;
                margin: 0 5px 0 0;
                display: block;
            }

            time {
                word-break: keep-all;
                color: #5843cf;
                display: inline-block;
                margin: 0 5px 0 0;
                flex-shrink: 0;
                width: 130px;
                transition: border $transition;
                border-bottom: solid 1px transparent
            }

            span {
                color: #e98b3f;
                margin: 0 5px 0 0;
                flex-shrink: 0;
                width: 50px;
                text-align: center;

                &.grey {
                    color: #888
                }
            }

            p {
                word-break: break-all;
                display: inline;
                padding: 0;
                color: #13aa38;
            }

            &.failed {
                background-color: #f3e7ea;
                border-top-color: #f5bbbe;

                svg { stroke: #ff1111 }
                time, span, p { color: #ff1111 }
                time:hover { border-color: #ff1111 }
            }
        }
    }
</style>
